import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import ChartPlaceholder from './ChartPlaceholder';

interface CashDemandProjectionDataPoint {
    readonly month: string;
    readonly contractsExpiring: number;
    readonly netGramsExpiring: number;
    readonly currentValueExpiring: number;
    readonly newValueExpiring: number;
    readonly monthlyAdditionalCash: number;
    readonly cumulativeAdditionalCash: number;
}

interface CashDemandProjectionChartProps {
    readonly data: readonly CashDemandProjectionDataPoint[];
    readonly loading?: boolean;
    readonly priceChangePercent: number;
    readonly currency?: 'EUR' | 'LEI';
}

const kAxisLabelFormatter: Highcharts.AxisLabelsFormatterCallbackFunction = function () {
    const value = Number(this.value);
    if (Math.abs(value) >= 1000) {
        return `${Math.round(value / 1000)}K`;
    }
    return String(value);
};

function CashDemandProjectionChart({
    data,
    loading = false,
    priceChangePercent,
    currency = 'EUR',
}: CashDemandProjectionChartProps): JSX.Element {
    const { t } = useTranslation();

    if (loading || data.length === 0) {
        return <ChartPlaceholder loading={loading} hasData={data.length > 0} />;
    }

    const currencyLabel = currency === 'LEI' ? 'RON' : 'EUR';

    const categories = data.map(d => d.month);
    const sign = priceChangePercent >= 0 ? '+' : '';

    const options: Highcharts.Options = {
        chart: {
            height: 450,
        },
        title: {
            text: t(
                'statistics.simulations.projectionChartTitle',
                `Cash Demand Projection (${sign}${priceChangePercent}% price change)`,
            ),
            style: { fontSize: '14px' },
        },
        xAxis: {
            categories,
            title: {
                text: t('statistics.simulations.month', 'Month'),
            },
            labels: {
                rotation: -45,
                style: { fontSize: '11px' },
            },
        },
        yAxis: [
            {
                title: {
                    text: t('statistics.simulations.value', 'Value') + ` (${currencyLabel})`,
                },
                labels: {
                    formatter: kAxisLabelFormatter,
                },
            },
            {
                title: {
                    text: t('statistics.simulations.contracts', 'Contracts'),
                },
                opposite: true,
                min: 0,
            },
        ],
        tooltip: {
            shared: true,
            valueDecimals: 0,
        },
        plotOptions: {
            column: {
                borderWidth: 0,
                borderRadius: 2,
            },
            area: {
                fillOpacity: 0.15,
            },
        },
        series: [
            {
                type: 'column',
                name:
                    t('statistics.simulations.monthlyAdditionalCash', 'Monthly Additional Cash') +
                    ` (${currencyLabel})`,
                data: data.map(d => d.monthlyAdditionalCash),
                color: '#faad14',
                yAxis: 0,
                tooltip: { valueSuffix: ` ${currencyLabel}` },
            },
            {
                type: 'area',
                name:
                    t('statistics.simulations.cumulativeCash', 'Cumulative Additional Cash') +
                    ` (${currencyLabel})`,
                data: data.map(d => d.cumulativeAdditionalCash),
                color: '#cf1322',
                yAxis: 0,
                tooltip: { valueSuffix: ` ${currencyLabel}` },
                marker: { enabled: true, radius: 3 },
            },
            {
                type: 'spline',
                name: t('statistics.simulations.contractsExpiring', 'Contracts Expiring'),
                data: data.map(d => d.contractsExpiring),
                color: '#1890ff',
                yAxis: 1,
                dashStyle: 'ShortDash',
                marker: { enabled: true, radius: 3 },
            },
        ],
        legend: {
            enabled: true,
            layout: 'horizontal',
            align: 'center',
            verticalAlign: 'bottom',
        },
        credits: {
            enabled: false,
        },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default CashDemandProjectionChart;
